import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeasons, useCreateLeague } from '../../features/leagues/api';
import { useAnnouncer } from '../../components/LiveAnnouncer';
import { usePageTitle } from '../../lib/usePageTitle';
import { AppLayout } from '../../components/AppLayout';
import { TextField } from '../../components/TextField';
import { Button } from '../../components/Button';
import { ApiError } from '../../lib/api';

/**
 * Wizard di creazione lega (lineeguida §24), condensato in 5 passi sensati.
 * Accessibilità: un passo alla volta, focus sul titolo del passo a ogni cambio,
 * avanzamento annunciato, validazione con messaggi espliciti, riepilogo finale.
 */

const STEPS = ['Nome', 'Campionato', 'Partecipanti', 'Regolamento', 'Conferma'] as const;

interface WizardState {
  name: string;
  seasonId: string;
  maxParticipants: number;
  botEnabled: boolean;
  botName: string;
}

export function CreateLeaguePage() {
  usePageTitle('Crea una lega');
  const navigate = useNavigate();
  const { announce } = useAnnouncer();
  const seasons = useSeasons();
  const createLeague = useCreateLeague();

  // Bozza del wizard: un reload a metà non deve far ripartire dal passo 1.
  const WIZARD_KEY = 'pc:bozza-lega';
  const [restored] = useState<{ step: number; state: WizardState } | null>(() => {
    try {
      const raw = localStorage.getItem(WIZARD_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as { step?: number; state?: WizardState };
      if (typeof parsed.step !== 'number' || !parsed.state) return null;
      return { step: Math.min(Math.max(parsed.step, 0), STEPS.length - 1), state: parsed.state };
    } catch {
      return null;
    }
  });

  const [step, setStep] = useState(restored?.step ?? 0);
  const [state, setState] = useState<WizardState>(
    restored?.state ?? {
      name: '',
      seasonId: '',
      maxParticipants: 20,
      botEnabled: true,
      botName: 'Cita',
    },
  );
  const [error, setError] = useState<string | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    // Salva la bozza solo quando c'è qualcosa da salvare.
    if (step === 0 && state.name === '') return;
    try {
      localStorage.setItem(WIZARD_KEY, JSON.stringify({ step, state }));
    } catch {
      // storage non disponibile: pazienza
    }
  }, [step, state]);

  // Al cambio passo: focus sul titolo, annuncio dell'avanzamento.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const goTo = (next: number) => {
    setError(null);
    setStep(next);
    announce(`Passo ${next + 1} di ${STEPS.length}: ${STEPS[next]}.`);
  };

  const validateCurrent = (): string | null => {
    if (step === 0 && state.name.trim().length < 3)
      return 'Il nome della lega deve avere almeno 3 caratteri.';
    if (step === 1 && !state.seasonId) return 'Seleziona un campionato per continuare.';
    if (step === 2 && (state.maxParticipants < 2 || state.maxParticipants > 100))
      return 'Il numero di partecipanti deve essere tra 2 e 100.';
    if (step === 3 && state.botEnabled && state.botName.trim().length === 0)
      return 'Dai un nome al giocatore bot, oppure disattivalo.';
    return null;
  };

  const next = () => {
    const problem = validateCurrent();
    if (problem) {
      setError(problem);
      announce(`Errore: ${problem}`, 'assertive');
      return;
    }
    goTo(step + 1);
  };

  const submit = async () => {
    setError(null);
    try {
      const res = await createLeague.mutateAsync({
        name: state.name.trim(),
        seasonId: state.seasonId,
        maxParticipants: state.maxParticipants,
        timezone: 'Europe/Rome',
        ...(state.botEnabled
          ? { botPlayer: { enabled: true, name: state.botName.trim() } }
          : {}),
      });
      announce(`Lega ${res.league.name} creata! Ora puoi invitare i partecipanti.`);
      try {
        localStorage.removeItem(WIZARD_KEY); // lega creata: bozza non più necessaria
      } catch {
        // ignora
      }
      navigate(`/leghe/${res.league.id}`);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Qualcosa è andato storto. Riprova.';
      setError(message);
      announce(`Errore: ${message}`, 'assertive');
    }
  };

  const seasonLabel = seasons.data?.find((s) => s.id === state.seasonId)?.label ?? '';

  return (
    <AppLayout title="Crea una lega">
      {/* Indicatore di avanzamento, visivo e semantico */}
      <p className="mt-2 text-ink-600 dark:text-ink-300">
        Passo {step + 1} di {STEPS.length}
      </p>
      {restored && (
        <p role="status" className="mt-2 text-ink-700 dark:text-ink-200">
          Abbiamo ripristinato la bozza che avevi iniziato.
        </p>
      )}
      <ol aria-hidden="true" className="mt-3 flex gap-2">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={`h-1.5 flex-1 rounded-full ${
              i <= step ? 'bg-panda-600' : 'bg-ink-200 dark:bg-ink-700'
            }`}
          />
        ))}
      </ol>

      <div className="mt-8 max-w-lg">
        <h2 ref={headingRef} tabIndex={-1} className="text-2xl font-bold tracking-tight">
          {STEPS[step]}
        </h2>

        {error && (
          <div
            role="alert"
            className="mt-4 rounded-lg border-2 border-danger bg-red-50 p-3 font-medium text-danger dark:bg-ink-800"
          >
            {error}
          </div>
        )}

        <div className="mt-6">
          {step === 0 && (
            <TextField
              label="Nome della lega"
              description="Il nome che tutti i partecipanti vedranno. Esempio: PandaCalcio Ascoli."
              required
              value={state.name}
              onChange={(e) => setState({ ...state, name: e.target.value })}
              autoFocus
            />
          )}

          {step === 1 && (
            <fieldset>
              <legend className="font-medium">
                Campionato <span className="text-danger">*</span>
              </legend>
              <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
                Le partite e le giornate della lega verranno da questo campionato.
              </p>
              {seasons.isLoading && <p role="status">Caricamento campionati…</p>}
              <div className="mt-3 flex flex-col gap-2" role="radiogroup" aria-label="Campionato">
                {seasons.data?.map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-center gap-3 rounded-xl border border-ink-300 p-4 has-[:checked]:border-panda-600 has-[:checked]:bg-panda-50 dark:border-ink-600 dark:has-[:checked]:bg-ink-800"
                  >
                    <input
                      type="radio"
                      name="season"
                      value={s.id}
                      checked={state.seasonId === s.id}
                      onChange={() => setState({ ...state, seasonId: s.id })}
                      className="h-5 w-5 accent-panda-700"
                    />
                    <span className="font-medium">{s.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {step === 2 && (
            <TextField
              label="Numero massimo di partecipanti"
              description="Da 2 a 100. Potrai cambiarlo in seguito."
              type="number"
              inputMode="numeric"
              min={2}
              max={100}
              required
              value={state.maxParticipants}
              onChange={(e) =>
                setState({ ...state, maxParticipants: Number(e.target.value) || 0 })
              }
            />
          )}

          {step === 3 && (
            <div className="flex flex-col gap-5">
              <p>
                La lega parte con il regolamento <strong>Panda Classic</strong>: pronostici su 4
                partite, risultato esatto, OVER, bonus e malus. Potrai personalizzare ogni regola
                dalle impostazioni della lega.
              </p>
              <fieldset>
                <legend className="font-medium">Giocatore bot</legend>
                <p className="mt-1 text-sm text-ink-600 dark:text-ink-300">
                  Un partecipante fittizio che gioca pronostici casuali a ogni giornata. Va in
                  classifica come gli altri, ma non conta per i bonus di originalità.
                </p>
                <label className="mt-3 flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={state.botEnabled}
                    onChange={(e) => setState({ ...state, botEnabled: e.target.checked })}
                    className="h-5 w-5 accent-panda-700"
                  />
                  <span className="font-medium">Attiva il giocatore bot</span>
                </label>
                {state.botEnabled && (
                  <div className="mt-3">
                    <TextField
                      label="Nome del bot"
                      value={state.botName}
                      onChange={(e) => setState({ ...state, botName: e.target.value })}
                    />
                  </div>
                )}
              </fieldset>
            </div>
          )}

          {step === 4 && (
            <section aria-label="Riepilogo della lega">
              <dl className="flex flex-col gap-3 rounded-2xl border border-ink-200 p-5 dark:border-ink-800">
                <div>
                  <dt className="text-sm text-ink-600 dark:text-ink-300">Nome</dt>
                  <dd className="font-semibold">{state.name}</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-600 dark:text-ink-300">Campionato</dt>
                  <dd className="font-semibold">{seasonLabel}</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-600 dark:text-ink-300">Partecipanti massimi</dt>
                  <dd className="font-semibold">{state.maxParticipants}</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-600 dark:text-ink-300">Regolamento</dt>
                  <dd className="font-semibold">Panda Classic v1</dd>
                </div>
                <div>
                  <dt className="text-sm text-ink-600 dark:text-ink-300">Giocatore bot</dt>
                  <dd className="font-semibold">
                    {state.botEnabled ? `Attivo (${state.botName})` : 'Disattivato'}
                  </dd>
                </div>
              </dl>
            </section>
          )}
        </div>

        <div className="mt-8 flex gap-3">
          {step > 0 && (
            <Button variant="secondary" onClick={() => goTo(step - 1)}>
              Indietro
            </Button>
          )}
          {step < STEPS.length - 1 && <Button onClick={next}>Continua</Button>}
          {step === STEPS.length - 1 && (
            <Button
              onClick={submit}
              loading={createLeague.isPending}
              loadingText="Creazione in corso…"
            >
              Crea la lega
            </Button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
